<template>
  <div>
    <button
      @click="showColumnConfig = !showColumnConfig"
      data-column-config
      class="tw-flex tw-text-xs tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-white tw-bg-secondary-500 tw-border tw-border-solid tw-border-secondary-600 tw-rounded-md hover:tw-bg-secondary-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-secondary-500 focus:tw-border-secondary-500"
    >
      <div class="tw-text-white">
        <CogIcon />
      </div>
      {{ t('common.forms.manage_columns') }}
    </button>

    <!-- Backdrop -->
    <Transition
      enter-active-class="tw-transition-opacity tw-duration-300"
      leave-active-class="tw-transition-opacity tw-duration-300"
      enter-from-class="tw-opacity-0"
      enter-to-class="tw-opacity-100"
      leave-from-class="tw-opacity-100"
      leave-to-class="tw-opacity-0"
    >
      <div
        v-if="showColumnConfig"
        class="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-[1050]"
        @click="showColumnConfig = false"
      ></div>
    </Transition>

    <!-- Offcanvas Panel -->
    <Transition
      enter-active-class="tw-transition-transform tw-duration-300 tw-ease-in-out"
      leave-active-class="tw-transition-transform tw-duration-300 tw-ease-in-out"
      enter-from-class="tw-translate-x-full"
      enter-to-class="tw-translate-x-0"
      leave-from-class="tw-translate-x-0"
      leave-to-class="tw-translate-x-full"
    >
      <div
        v-if="showColumnConfig"
        class="tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-96 tw-bg-white tw-shadow-xl tw-z-[1051]"
      >
        <!-- Header -->
        <div
          class="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-gray-200 tw-bg-gray-50"
        >
          <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900">
            {{ t('common.forms.manage_columns') }}
          </h2>
          <button
            @click="showColumnConfig = false"
            class="tw-p-2 tw-text-gray-400 hover:tw-text-gray-600 tw-rounded-md hover:tw-bg-gray-100 tw-transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              color="currentColor"
              fill="none"
            >
              <path
                d="M19.0005 4.99988L5.00049 18.9999M5.00049 4.99988L19.0005 18.9999"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <!-- Content -->
        <div class="tw-p-4 tw-max-h-[calc(100vh-10rem)] tw-overflow-y-auto tw-pb-4">
          <!-- Fixed Columns Section -->
          <div class="tw-mb-6">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
              <h3 class="tw-text-sm tw-font-medium tw-text-gray-700">
                {{ t('common.forms.fixed_columns') }}
              </h3>
              <span
                class="tw-text-xs tw-text-gray-500 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-full"
              >
                {{ pinnedColumns.length }}/{{ Math.min(3, availableColumns.length) }}
              </span>
            </div>

            <p class="tw-text-xs tw-text-gray-500 tw-mb-3">
              {{ t('common.forms.fixed_columns_description') }}
            </p>

            <draggable
              v-model="pinnedColumns"
              :component-data="{
                tag: 'div',
                type: 'transition-group',
                name: !isDragging ? 'flip-list' : null,
              }"
              :item-key="getColumnKey"
              handle=".drag-handle"
              :animation="150"
              ghost-class="column-ghost"
              chosen-class="column-chosen"
              drag-class="column-drag"
              :group="{
                name: 'columns',
                pull: true,
                put: canAcceptPinnedItem,
              }"
              @start="onDragStart"
              @end="onDragEnd"
              @change="onPinnedColumnsChange"
              data-container="pinned-columns"
              class="tw-space-y-2 tw-min-h-[60px] tw-border-2 tw-border-dashed tw-border-gray-200 tw-rounded-lg tw-p-3"
              :class="{
                'tw-border-blue-300 tw-bg-blue-50': isDragging && !isFixedAreaFull,
                'tw-border-red-300 tw-bg-red-50': isDragging && isFixedAreaFull,
                'tw-bg-gray-50': pinnedColumns.length === 0 && !isDragging,
              }"
            >
              <template #item="{ element: column }">
                <div
                  :key="column.id"
                  class="column-item tw-border tw-border-transparent tw-rounded-lg tw-transition-all tw-select-none"
                >
                  <div
                    class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors tw-bg-white tw-border tw-border-gray-200 tw-shadow-sm"
                  >
                    <!-- Drag handle -->
                    <div
                      class="drag-handle tw-text-gray-400 hover:tw-text-gray-600 tw-flex-shrink-0 tw-transition-colors tw-cursor-move"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        color="currentColor"
                        fill="none"
                      >
                        <path
                          d="M18 8V8.00635M12 8V8.00635M6 8L6 8.00635M18 15.9937V16M12 15.9937V16M6 15.9937L6 16"
                          stroke="currentColor"
                          stroke-width="2.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <div class="tw-flex-1 tw-select-text">
                      <ColumnInSettings :column="column" />
                    </div>
                  </div>
                </div>
              </template>
            </draggable>
          </div>

          <!-- Divider -->
          <div class="tw-border-b tw-border-gray-200 tw-mb-6"></div>

          <!-- Regular Columns Section -->
          <div>
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
              <h3 class="tw-text-sm tw-font-medium tw-text-gray-700">
                {{ t('common.forms.available_columns') }}
              </h3>
              <span
                class="tw-text-xs tw-text-gray-500 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-full"
              >
                {{ unpinnedColumns.length }}/{{ availableColumns.length }}
              </span>
            </div>

            <p class="tw-text-xs tw-text-gray-500 tw-mb-3">
              {{ t('common.forms.available_columns_description') }}
            </p>

            <draggable
              v-model="unpinnedColumns"
              :component-data="{
                tag: 'div',
                type: 'transition-group',
                name: !isDragging ? 'flip-list' : null,
              }"
              :item-key="getColumnKey"
              handle=".drag-handle"
              :animation="150"
              ghost-class="column-ghost"
              chosen-class="column-chosen"
              drag-class="column-drag"
              :group="{
                name: 'columns',
                pull: true,
                put: true,
              }"
              @start="onDragStart"
              @end="onDragEnd"
              @change="onUnpinnedColumnsChange"
              data-container="unpinned-columns"
              class="tw-space-y-2"
            >
              <template #item="{ element: column }">
                <div
                  :key="column.id"
                  class="column-item tw-border tw-border-transparent tw-rounded-lg tw-transition-all tw-select-none"
                >
                  <div
                    class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors tw-bg-white tw-border tw-border-gray-100"
                  >
                    <!-- Drag handle -->
                    <div
                      class="drag-handle tw-text-gray-400 hover:tw-text-gray-600 tw-flex-shrink-0 tw-transition-colors tw-cursor-move"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        color="currentColor"
                        fill="none"
                      >
                        <path
                          d="M18 8V8.00635M12 8V8.00635M6 8L6 8.00635M18 15.9937V16M12 15.9937V16M6 15.9937L6 16"
                          stroke="currentColor"
                          stroke-width="2.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <div class="tw-flex-1 tw-select-text">
                      <ColumnInSettings :column="column" />
                    </div>
                  </div>
                </div>
              </template>
            </draggable>
          </div>
        </div>

        <!-- Footer with actions -->
        <div
          class="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-p-4 tw-border-t tw-border-gray-200 tw-bg-gray-50"
        >
          <button
            @click="resetColumnVisibility"
            class="tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-teal-600 tw-border tw-border-teal-200 tw-rounded-md hover:tw-bg-teal-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-teal-500 tw-transition-colors"
          >
            {{ t('common.forms.reset_columns') }}
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts" generic="T">
import { useI18n } from 'vue-i18n';
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import type { Table, VisibilityState, Column } from '@tanstack/vue-table';
import type { FancyTableColumnDef } from './types';
import ColumnInSettings from './ColumnInSettings.vue';
import draggable from 'vuedraggable';
import CogIcon from '@/assets/icons/Cog.svg';

const { t } = useI18n();

const props = defineProps<{
  table: Table<T>;
}>();

const showColumnConfig = ref(false);
const isDragging = ref(false);

// Create reactive refs for pinned and unpinned columns
const pinnedColumns = ref<Column<T, unknown>[]>([]);
const unpinnedColumns = ref<Column<T, unknown>[]>([]);

// Track if we're trying to drop into a full fixed area
const isFixedAreaFull = computed(() => pinnedColumns.value.length >= 3);

// Get all configurable columns
const getConfigurableColumns = () => {
  return props.table
    .getAllColumns()
    .filter(col => !(col.columnDef as FancyTableColumnDef<T>).hideFromColumnConfig);
};

// Get all available columns (configurable columns)
const availableColumns = computed(() => getConfigurableColumns());

// Get unique key for draggable items
const getColumnKey = (column: Column<T, unknown>) => {
  return column.id;
};

// Initialize and update pinned/unpinned columns based on table state
const updateColumnArrays = () => {
  // Don't update while dragging
  if (isDragging.value) return;

  const allColumns = getConfigurableColumns();
  const currentPinning = props.table.getState().columnPinning;
  const currentOrder = props.table.getState().columnOrder;
  const leftPinnedIds = currentPinning.left || [];

  // Create maps for quick lookup
  const columnMap = new Map(allColumns.map(col => [col.id, col]));

  // Get columns in the correct order (either custom order or original order)
  let orderedColumns: Column<T, unknown>[];
  if (currentOrder && currentOrder.length > 0) {
    // Use custom column order
    orderedColumns = currentOrder
      .map(id => columnMap.get(id))
      .filter((col): col is Column<T, unknown> => col !== undefined);

    // Add any missing columns that weren't in the custom order
    const orderedIds = new Set(currentOrder);
    const missingColumns = allColumns.filter(col => !orderedIds.has(col.id));
    orderedColumns.push(...missingColumns);
  } else {
    // Use original order
    orderedColumns = allColumns;
  }

  // Update pinned columns in the correct order
  const newPinnedColumns: Column<T, unknown>[] = [];
  leftPinnedIds.forEach(id => {
    const col = columnMap.get(id);
    if (col) {
      newPinnedColumns.push(col);
    }
  });

  // Update unpinned columns in the correct order (excluding pinned ones)
  const newUnpinnedColumns = orderedColumns.filter(col => !leftPinnedIds.includes(col.id));

  pinnedColumns.value = newPinnedColumns;
  unpinnedColumns.value = newUnpinnedColumns;
};

// Handle drag start
const onDragStart = () => {
  isDragging.value = true;
};

// Check if pinned area can accept more items
const canAcceptPinnedItem = () => {
  return pinnedColumns.value.length < 3;
};

// Handle changes to pinned columns
const onPinnedColumnsChange = (event: any) => {
  if (event.added) {
    // Column was added to pinned section
    updateColumnPinning();
  } else if (event.removed) {
    // Column was removed from pinned section
    updateColumnPinning();
  } else if (event.moved) {
    // Column was reordered within pinned section
    updateColumnPinning();
  }
};

// Handle changes to unpinned columns
const onUnpinnedColumnsChange = (event: any) => {
  if (event.added) {
    // Column was added to unpinned section (moved from pinned)
    updateColumnPinning();
  } else if (event.removed) {
    // Column was removed from unpinned section (moved to pinned)
    updateColumnPinning();
  } else if (event.moved) {
    // Column was reordered within unpinned section
    updateColumnOrder();
  }
};

// Update column pinning state based on current arrays
const updateColumnPinning = () => {
  const currentPinning = props.table.getState().columnPinning;
  const newPinnedIds = pinnedColumns.value.map(col => col.id);

  // Update pinning state (keep right pinning intact, only update left)
  const newPinning = {
    left: newPinnedIds,
    right: currentPinning.right || [],
  };

  props.table.setColumnPinning(newPinning);

  // Also update column order to reflect the new arrangement
  updateColumnOrder();
};

// Update column order based on current arrays
const updateColumnOrder = () => {
  const allColumnsInOrder = [
    ...pinnedColumns.value.map(col => col.id),
    ...unpinnedColumns.value.map(col => col.id),
  ];

  // Add any right-pinned columns at the end
  const currentPinning = props.table.getState().columnPinning;
  const rightPinnedIds = currentPinning.right || [];
  const rightPinnedColumns = rightPinnedIds.filter(id => !allColumnsInOrder.includes(id));

  const newColumnOrder = [...allColumnsInOrder, ...rightPinnedColumns];
  props.table.setColumnOrder(newColumnOrder);
};

// Handle drag end event
const onDragEnd = () => {
  isDragging.value = false;
  // Immediately update column arrays to reflect the new order
  updateColumnArrays();
};

// Watch for changes in table state and update column arrays
watch(
  () => [
    props.table.getState().columnPinning,
    props.table.getState().columnOrder,
    props.table.getAllColumns().length,
  ],
  () => {
    if (!isDragging.value) {
      updateColumnArrays();
    }
  },
  { deep: true, immediate: true }
);

// Reset column visibility and order to defaults
const resetColumnVisibility = () => {
  // Reset visibility
  const resetState: VisibilityState = {};
  props.table.getAllColumns().forEach(column => {
    if (column.getCanHide()) {
      resetState[column.id] = true;
    }
  });
  props.table.setColumnVisibility(resetState);

  // Reset column order to default
  props.table.setColumnOrder([]);

  // Reset column pinning to default (no pinned columns)
  props.table.setColumnPinning({
    left: [],
    right: [],
  });

  // Update will happen automatically via watcher
};

// Close offcanvas when clicking outside
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement;
  const offcanvas = document.querySelector('.tw-fixed.tw-top-0.tw-right-0.tw-h-full');
  const button = document.querySelector('button[data-column-config]');

  if (offcanvas && !offcanvas.contains(target) && !button?.contains(target)) {
    showColumnConfig.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}

.column-ghost {
  opacity: 0.5;
  background: #f0f9ff;
  border: 2px dashed #3b82f6;
  transform: rotate(2deg);
}

.column-chosen {
  background: #eff6ff;
  border-color: #60a5fa;
  transform: scale(1.02);
}

.column-drag {
  transform: rotate(2deg);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
  background: white;
  border: 2px solid #3b82f6;
  z-index: 1051;
}

.column-item {
  transition: all 0.3s ease;
}

.column-item:hover {
  transform: translateX(2px);
}

.flip-list-enter-active,
.flip-list-leave-active {
  transition: all 0.5s ease;
}

.flip-list-enter-from,
.flip-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.flip-list-leave-active {
  position: absolute;
}
</style>
